import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import api, { authAPI } from '../services/api';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerHeader from '../components/ManagerHeader';
import ManagerMobileSidebar from '../components/ManagerMobileSidebar';
import Footer from '../components/Footer';
import LogoutModal from '../components/LogoutModal';
import SuccessModal from '../components/SuccessModal';
import { scheduleLoadingEnd } from '../utils/loadingDelay';

interface Merchant {
  id: number;
  ownerName: string;
  email: string;
  wallet: number;
  status: 'active' | 'inactive' | 'banned';
  createdAt: string;
  addMoneyEnabled?: boolean;
}

interface MerchantFormData {
  name: string;
  email: string;
  password: string;
  phone: string;
  client_id: string;
  client_secret: string;
  api_key: string;
  viyonaEnabled: boolean;
  poolpayEnabled: boolean;
  addMoneyEnabled: boolean;
}

interface ManagerMerchantsProps {
  // Add any props if needed
}

const ManagerMerchants: React.FC<ManagerMerchantsProps> = () => {
  const { user, logout } = useAuth();
  const { sidebarColor, contentColor, sectionColor, tableColor } = useManagerTheme();
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('merchants');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showCreateMerchantModal, setShowCreateMerchantModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState<any>(null);
  const [successModalTitle, setSuccessModalTitle] = useState('Success!');
  const [successModalMessage, setSuccessModalMessage] = useState('Operation completed successfully.');
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [showEditMerchantModal, setShowEditMerchantModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(null);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [selectedFilter, setSelectedFilter] = useState('All Payouts');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(2);
  const [totalMerchants, setTotalMerchants] = useState(0);
  const [merchantFormData, setMerchantFormData] = useState<MerchantFormData>({
    name: '',
    email: '',
    password: '',
    phone: '',
    client_id: '',
    client_secret: '',
    api_key: '',
    viyonaEnabled: true,
    poolpayEnabled: true,
    addMoneyEnabled: true
  });
  const [formErrors, setFormErrors] = useState<Partial<MerchantFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [addMoneyData, setAddMoneyData] = useState({
    amount: '',
    description: '',
    type: 'credit' as 'credit' | 'debit'
  });
  const [managerWalletBalance, setManagerWalletBalance] = useState(0);
  const [managerAccountBalance, setManagerAccountBalance] = useState(0);
  const [managerClientId, setManagerClientId] = useState<string>('');
  const [managerClientSecret, setManagerClientSecret] = useState<string>('');
  const [managerApiKey, setManagerApiKey] = useState<string>('');
  const [editMerchantData, setEditMerchantData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    panNumber: '',
    gstNumber: '',
    bankAccountNumber: '',
    ifscCode: '',
    bankName: '',
    accountHolderName: '',
    isActive: true,
    newPassword: '',
    confirmPassword: '',
    clientId: '',
    clientSecret: '',
    apiKey: '',
    viyonaEnabled: true,
    poolpayEnabled: true,
    addMoneyEnabled: true,
    adminControlsPayoutOptions: false
  });
  const [changePasswordData, setChangePasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showBanConfirmModal, setShowBanConfirmModal] = useState(false);
  const [merchantToBan, setMerchantToBan] = useState<{ id: number; name: string } | null>(null);
  const [showUnbanConfirmModal, setShowUnbanConfirmModal] = useState(false);
  const [merchantToUnban, setMerchantToUnban] = useState<{ id: number; name: string } | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [merchantToDelete, setMerchantToDelete] = useState<{ id: number; name: string } | null>(null);
  const [openActionsId, setOpenActionsId] = useState<number | null>(null);
  const [actionsAnchorRect, setActionsAnchorRect] = useState<{ top: number; left: number; right: number; bottom: number } | null>(null);
  const loadingStartRef = useRef(0);

  useEffect(() => {
    fetchMerchantsData();
    fetchManagerWalletBalance();
    fetchManagerProfile();
  }, [currentPage]);

  // Fetch manager's profile (Client ID, Client Secret, API Key) for Create/Edit modals
  const fetchManagerProfile = async () => {
    try {
      const profileResponse = await authAPI.getProfile();
      const profile = profileResponse.data?.data;
      if (profile) {
        setManagerClientId(profile.client_id || '');
        setManagerClientSecret(profile.client_secret || '');
        setManagerApiKey(profile.api_key || '');
      }
    } catch (error) {
      console.error('Error fetching manager profile:', error);
    }
  };

  // Fetch manager's wallet balance and account balance
  const fetchManagerWalletBalance = async () => {
    try {
      const response = await api.get('/dashboard/manager/stats');
      console.log('Manager balance API response:', response.data);
      if (response.data.success) {
        const walletBalance = Number(response.data.data.managerWalletBalance) || 0;
        const accountBalance = Number(response.data.data.managerAccountBalance) || 0;
        console.log('Setting manager wallet balance to:', walletBalance);
        console.log('Setting manager account balance to:', accountBalance);
        setManagerWalletBalance(walletBalance);
        setManagerAccountBalance(accountBalance);
      }
    } catch (error) {
      console.error('Error fetching manager wallet balance:', error);
    }
  };

  // Mock data for fallback
  const mockMerchants: Merchant[] = [
    {
      id: 102,
      ownerName: 'Manual9',
      email: 'Manual9@stackspire.com',
      wallet: 149252.55,
      status: 'active',
      createdAt: '2025-01-10'
    },
    {
      id: 101,
      ownerName: 'Manual8',
      email: 'Manual8@stackspire.com',
      wallet: 92937.97,
      status: 'active',
      createdAt: '2025-01-09'
    },
    {
      id: 100,
      ownerName: 'Manual7',
      email: 'Manual7@stackspire.com',
      wallet: 225999.39,
      status: 'active',
      createdAt: '2025-01-08'
    },
    {
      id: 99,
      ownerName: 'Manual6',
      email: 'Manual6@stackspire.com',
      wallet: 68383.46,
      status: 'active',
      createdAt: '2025-01-07'
    },
    {
      id: 89,
      ownerName: 'Manual4',
      email: 'Manual4@stackspire.com',
      wallet: 59457.49,
      status: 'active',
      createdAt: '2025-01-06'
    },
    {
      id: 88,
      ownerName: 'Manual3',
      email: 'Manual3@stackspire.com',
      wallet: 61182.29,
      status: 'active',
      createdAt: '2025-01-05'
    },
    {
      id: 87,
      ownerName: 'Manual2',
      email: 'manual2@stackspire.com',
      wallet: 39722.55,
      status: 'active',
      createdAt: '2025-01-04'
    },
    {
      id: 86,
      ownerName: 'Stackspire',
      email: 'finance.support@stackspire.com',
      wallet: 53681.04,
      status: 'active',
      createdAt: '2025-01-03'
    },
    {
      id: 85,
      ownerName: 'LOTUSEXCH33',
      email: 'enterpriseshn82@gmail.com',
      wallet: 202462.3,
      status: 'active',
      createdAt: '2025-01-02'
    }
  ];

  const fetchMerchantsData = async () => {
    try {
      loadingStartRef.current = Date.now();
      setLoading(true);
      
      // Fetch from real API
      const response = await api.get('/merchants', {
        params: {
          page: currentPage,
          limit: 9
        }
      });
      
      if (response.data.success) {
        const merchantsData = response.data.data.map((merchant: any) => ({
          id: merchant.id,
          ownerName: merchant.ownerName,
          email: merchant.email,
          wallet: merchant.wallet || 0,
          status: merchant.status ? 'active' : 'banned',
          createdAt: merchant.created_at ? merchant.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
          addMoneyEnabled: merchant.add_money_enabled !== 0 && merchant.add_money_enabled !== false
        }));
        
        setMerchants(merchantsData);
        setTotalPages(response.data.pagination.pages);
        const total = response.data.pagination?.total ?? (response.data.pagination.pages === 1 ? merchantsData.length : response.data.pagination.pages * 9);
        setTotalMerchants(total);
      } else {
        throw new Error(response.data.message || 'Failed to fetch merchants');
      }
    } catch (error) {
      console.error('Error fetching merchants data:', error);
      // Fallback to mock data if API fails
      setMerchants(mockMerchants);
      const mockPages = Math.ceil(mockMerchants.length / 9);
      setTotalPages(mockPages);
      setTotalMerchants(mockMerchants.length);
    } finally {
      scheduleLoadingEnd(loadingStartRef.current, setLoading);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const managerSidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'account', label: 'My Account', icon: '🏦' },
    { id: 'payouts', label: 'Payouts', icon: '📋' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    { id: 'merchants', label: 'Merchants', icon: '🏪' },
    { id: 'escrow', label: 'Escrow Transactions', icon: '🔒' },
    { id: 'credit', label: 'Credit History', icon: '💳' }
  ];

  const quickFilters = [
    { id: 'all', label: 'All Payouts', icon: '✓' },
    { id: 'scheduled', label: 'Scheduled for next 2 days', icon: '' },
    { id: 'queued', label: 'Queued (RazorpayX A/c) 1', icon: '' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleCreateMerchantClick = async () => {
    // Fetch manager's profile to get Client ID, Client Secret, and API Key
    try {
      const profileResponse = await authAPI.getProfile();
      const profile = profileResponse.data.data;
      setManagerClientId(profile.client_id || '');
      setManagerClientSecret(profile.client_secret || '');
      setManagerApiKey(profile.api_key || '');
      
      // Pre-populate the form with manager's credentials
      setMerchantFormData(prev => ({
        ...prev,
        client_id: profile.client_id || '',
        client_secret: profile.client_secret || '',
        api_key: profile.api_key || ''
      }));
    } catch (error) {
      console.error('Error fetching manager profile:', error);
      // Still open the modal even if profile fetch fails
    }
    setShowCreateMerchantModal(true);
  };

  const handleCloseCreateMerchantModal = () => {
    setShowCreateMerchantModal(false);
    setMerchantFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      client_id: '',
      client_secret: '',
      api_key: '',
      viyonaEnabled: true,
      poolpayEnabled: true,
      addMoneyEnabled: true
    });
    setFormErrors({});
  };

  const handleCloseAddMoneyModal = () => {
    setShowAddMoneyModal(false);
    setAddMoneyData({
      amount: '',
      description: '',
      type: 'credit'
    });
    setSelectedMerchantId(null);
    setSelectedMerchant(null);
  };

  const handleCloseEditMerchantModal = () => {
    setShowEditMerchantModal(false);
    setEditMerchantData({
      name: '',
      email: '',
      phone: '',
      businessName: '',
      businessType: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      panNumber: '',
      gstNumber: '',
      bankAccountNumber: '',
      ifscCode: '',
      bankName: '',
      accountHolderName: '',
      isActive: true,
      newPassword: '',
      confirmPassword: '',
      clientId: '',
      clientSecret: '',
      apiKey: '',
      viyonaEnabled: true,
      poolpayEnabled: true,
      addMoneyEnabled: true,
      adminControlsPayoutOptions: false
    });
    setSelectedMerchantId(null);
    setSelectedMerchant(null);
  };

  const handleCloseChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setChangePasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setSelectedMerchantId(null);
    setSelectedMerchant(null);
  };

  const handleInputChange = (field: keyof MerchantFormData, value: string | boolean) => {
    setMerchantFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<MerchantFormData> = {};

    console.log('Validating form data:', merchantFormData);

    if (!merchantFormData.name.trim()) {
      errors.name = 'Full name is required';
    }

    if (!merchantFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(merchantFormData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    if (!merchantFormData.password.trim()) {
      errors.password = 'Password is required';
    } else if (merchantFormData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }

    if (!merchantFormData.phone.trim()) {
      errors.phone = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(merchantFormData.phone.trim())) {
      errors.phone = 'Please enter a valid 10-digit mobile number';
    }

    console.log('Validation errors:', errors);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitMerchant = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form data before validation:', merchantFormData);
    
    if (!validateForm()) {
      console.log('Validation failed, errors:', formErrors);
      return;
    }

    console.log('Form data being sent to API:', {
      name: merchantFormData.name,
      email: merchantFormData.email,
      password: merchantFormData.password,
      phone: merchantFormData.phone,
      note: 'Client ID and Client Secret will be automatically set from manager\'s profile'
    });

    setSubmitting(true);
    
    try {
      // Make API call to create merchant
      // Note: Client ID and Client Secret are automatically set from manager's profile on backend
      const response = await api.post('/merchants', {
        name: merchantFormData.name,
        email: merchantFormData.email,
        password: merchantFormData.password,
        phone: merchantFormData.phone,
        viyona_enabled: merchantFormData.viyonaEnabled,
        poolpay_enabled: merchantFormData.poolpayEnabled,
        add_money_enabled: merchantFormData.addMoneyEnabled
      });
      
      if (response.data.success) {
        // Refresh merchants list
        await fetchMerchantsData();
        
        // Close create modal and reset form
        handleCloseCreateMerchantModal();
        
        // Show beautiful success modal
        setSuccessModalTitle('Merchant Created Successfully!');
        setSuccessModalMessage('The new merchant account has been created and is ready to use. The merchant will use your (Manager\'s) Client ID, Client Secret, and API Key for API operations.');
        const successData: any[] = [
          {
            label: 'Merchant ID',
            value: response.data.data.id,
            type: 'id'
          },
          {
            label: 'Name',
            value: response.data.data.name,
            type: 'text'
          },
          {
            label: 'Email',
            value: response.data.data.email,
            type: 'email'
          }
        ];
        
        // Add Manager's Client ID, Client Secret, and API Key that were assigned to the merchant
        if (merchantFormData.client_id) {
          successData.push({
            label: 'Client ID (from Manager)',
            value: merchantFormData.client_id,
            type: 'text',
            copyable: true
          });
        }
        if (merchantFormData.client_secret) {
          successData.push({
            label: 'Client Secret (from Manager)',
            value: merchantFormData.client_secret,
            type: 'text',
            copyable: true
          });
        }
        if (merchantFormData.api_key) {
          successData.push({
            label: 'API Key (from Manager)',
            value: merchantFormData.api_key,
            type: 'text',
            copyable: true
          });
        }
        
        setSuccessModalData(successData);
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to create merchant');
      }
      
    } catch (error: any) {
      console.error('Error creating merchant:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error creating merchant. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMoneySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!addMoneyData.amount || parseFloat(addMoneyData.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!selectedMerchantId) {
      alert('No merchant selected');
      return;
    }

    // Check if manager has sufficient balance for credit transactions
    if (addMoneyData.type === 'credit') {
      const transactionAmount = parseFloat(addMoneyData.amount);
      if (Number(managerWalletBalance) < transactionAmount) {
        alert(`Insufficient manager wallet balance. Available: ₹${Number(managerWalletBalance).toFixed(2)}, Required: ₹${transactionAmount.toFixed(2)}`);
        return;
      }
    }

    setSubmitting(true);

    try {
      // Make API call to add money to merchant's wallet
      const response = await api.post(`/merchants/${selectedMerchantId}/wallet/add-money`, {
        amount: parseFloat(addMoneyData.amount),
        description: addMoneyData.description,
        type: addMoneyData.type
      });

      if (response.data.success) {
        // Close modal and refresh data
        handleCloseAddMoneyModal();
        await fetchMerchantsData();
        await fetchManagerWalletBalance();

        // Show success modal
        setSuccessModalTitle('Wallet Transaction Successful!');
        setSuccessModalMessage(`Money ${addMoneyData.type === 'credit' ? 'added to' : 'deducted from'} merchant wallet successfully.`);
        setSuccessModalData([
          {
            label: 'Transaction ID',
            value: response.data.data.transactionId,
            type: 'code',
            copyable: true
          },
          {
            label: 'Transaction Type',
            value: addMoneyData.type === 'credit' ? 'Credit' : 'Debit',
            type: 'text'
          },
          {
            label: 'Amount',
            value: parseFloat(addMoneyData.amount),
            type: 'amount'
          },
          {
            label: 'Previous Balance',
            value: response.data.data.previousBalance,
            type: 'amount'
          },
          {
            label: 'New Balance',
            value: response.data.data.newBalance,
            type: 'amount'
          },
          {
            label: 'Description',
            value: response.data.data.description,
            type: 'text'
          },
          {
            label: 'Merchant',
            value: selectedMerchant?.name || 'Unknown',
            type: 'text'
          }
        ]);
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to process wallet transaction');
      }

    } catch (error: any) {
      console.error('Error adding money:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error adding money. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditMerchantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedMerchantId) {
      alert('No merchant selected');
      return;
    }

    // Validate password if provided
    if (editMerchantData.newPassword && editMerchantData.newPassword !== editMerchantData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      // API expects snake_case; ensure phone is string (API may return number)
      const phoneStr = editMerchantData.phone != null ? String(editMerchantData.phone).trim() : '';
      const updateData: Record<string, string | boolean | undefined> = {
        name: editMerchantData.name?.trim() || undefined,
        phone: phoneStr || undefined,
        business_name: editMerchantData.businessName?.trim() || undefined,
        business_type: editMerchantData.businessType?.trim() || undefined,
        address: editMerchantData.address?.trim() || undefined,
        city: editMerchantData.city?.trim() || undefined,
        state: editMerchantData.state?.trim() || undefined,
        pincode: editMerchantData.pincode?.trim() || undefined,
        pan_number: (editMerchantData.panNumber || '').toUpperCase().trim() || undefined,
        gst_number: (editMerchantData.gstNumber || '').toUpperCase().trim() || undefined,
        bank_account_number: editMerchantData.bankAccountNumber?.trim() || undefined,
        ifsc_code: (editMerchantData.ifscCode || '').toUpperCase().trim() || undefined,
        bank_name: editMerchantData.bankName?.trim() || undefined,
        account_holder_name: editMerchantData.accountHolderName?.trim() || undefined
      };
      // Only include payout toggles when Manager is allowed to change them
      if (!editMerchantData.adminControlsPayoutOptions) {
        updateData.viyona_enabled = Boolean(editMerchantData.viyonaEnabled);
        updateData.poolpay_enabled = Boolean(editMerchantData.poolpayEnabled);
        updateData.add_money_enabled = Boolean(editMerchantData.addMoneyEnabled);
      }

      // Only include password if provided
      if (editMerchantData.newPassword?.trim()) {
        updateData.newPassword = editMerchantData.newPassword.trim();
      }

      // Remove undefined so API gets only defined fields
      const payload = Object.fromEntries(Object.entries(updateData).filter(([, v]) => v !== undefined));

      // Make API call to update merchant
      const response = await api.put(`/merchants/${selectedMerchantId}`, payload);

      if (response.data.success) {
        // Close modal and refresh data
        handleCloseEditMerchantModal();
        await fetchMerchantsData();

        // Show success modal
        setSuccessModalTitle('Merchant Updated Successfully!');
        setSuccessModalMessage('The merchant details have been updated successfully.');
        setSuccessModalData([
          {
            label: 'Merchant ID',
            value: selectedMerchantId,
            type: 'id'
          },
          {
            label: 'Updated Name',
            value: editMerchantData.name,
            type: 'text'
          },
          {
            label: 'Status',
            value: editMerchantData.isActive ? 'Active' : 'Inactive',
            type: 'text'
          }
        ]);
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to update merchant');
      }

    } catch (error: any) {
      console.error('Error updating merchant:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating merchant. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'home') {
      window.location.href = '/manager';
    }
    // For other tabs, you can add navigation logic here
  };

  const handleCreditMoney = () => {
    console.log('Credit Money clicked');
    // Implement credit money functionality
  };

  const handleMyProfile = () => {
    console.log('My Profile clicked');
    // Implement profile functionality
  };

  const handleFilterSelect = (filterId: string) => {
    setSelectedFilter(filterId);
    console.log('Filter selected:', filterId);
    // Implement filter functionality
  };

  const handleAddMoney = async (merchantId: number) => {
    try {
      // Fetch merchant details
      const response = await api.get(`/merchants/${merchantId}`);
      if (response.data.success) {
        setSelectedMerchantId(merchantId);
        setSelectedMerchant(response.data.data);
        setAddMoneyData({
          amount: '',
          description: '',
          type: 'credit'
        });
        setShowAddMoneyModal(true);
      }
    } catch (error) {
      console.error('Error fetching merchant details:', error);
      alert('Error fetching merchant details');
    }
  };

  const handleEditMerchant = async (merchantId: number) => {
    try {
      // Fetch merchant details
      const response = await api.get(`/merchants/${merchantId}`);
      if (response.data.success) {
        const merchant = response.data.data;
        setSelectedMerchantId(merchantId);
        setSelectedMerchant(merchant);
        setEditMerchantData({
          name: merchant.name || '',
          email: merchant.email || '',
          phone: merchant.phone != null ? String(merchant.phone) : (merchant.phone_number != null ? String(merchant.phone_number) : ''),
          businessName: merchant.business_name || '',
          businessType: merchant.business_type || '',
          address: merchant.address || '',
          city: merchant.city || '',
          state: merchant.state || '',
          pincode: merchant.pincode || '',
          panNumber: merchant.pan_number || '',
          gstNumber: merchant.gst_number || '',
          bankAccountNumber: merchant.bank_account_number || '',
          ifscCode: merchant.ifsc_code || '',
          bankName: merchant.bank_name || '',
          accountHolderName: merchant.account_holder_name || '',
          isActive: merchant.is_active,
          newPassword: '',
          confirmPassword: '',
          clientId: merchant.client_id != null ? String(merchant.client_id) : '',
          clientSecret: merchant.client_secret != null ? String(merchant.client_secret) : '',
          apiKey: merchant.api_key != null ? String(merchant.api_key) : '',
          viyonaEnabled: merchant.viyona_enabled !== 0 && merchant.viyona_enabled !== false,
          poolpayEnabled: merchant.poolpay_enabled !== 0 && merchant.poolpay_enabled !== false,
          addMoneyEnabled: merchant.add_money_enabled !== 0 && merchant.add_money_enabled !== false,
          adminControlsPayoutOptions: merchant.admin_controls_payout_options === 1 || merchant.admin_controls_payout_options === true
        });
        setShowEditMerchantModal(true);
      }
    } catch (error) {
      console.error('Error fetching merchant details:', error);
      alert('Error fetching merchant details');
    }
  };

  const handleChangePassword = async (merchantId: number) => {
    try {
      // Fetch merchant details
      const response = await api.get(`/merchants/${merchantId}`);
      if (response.data.success) {
        const merchant = response.data.data;
        setSelectedMerchantId(merchantId);
        setSelectedMerchant(merchant);
        setChangePasswordData({
          newPassword: '',
          confirmPassword: ''
        });
        setShowChangePasswordModal(true);
      }
    } catch (error) {
      console.error('Error fetching merchant details:', error);
      alert('Error fetching merchant details');
    }
  };

  const openActionsDropdown = (merchantId: number, event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionsAnchorRect(rect);
    setOpenActionsId(merchantId);
  };

  const closeActionsDropdown = () => {
    setOpenActionsId(null);
    setActionsAnchorRect(null);
  };

  const handleActionsEdit = (merchantId: number) => {
    closeActionsDropdown();
    handleEditMerchant(merchantId);
  };

  const handleActionsBan = (merchantId: number) => {
    closeActionsDropdown();
    const m = merchants.find((x) => x.id === merchantId);
    setMerchantToBan({ id: merchantId, name: m?.ownerName || `Merchant #${merchantId}` });
    setShowBanConfirmModal(true);
  };

  const handleCloseBanConfirmModal = () => {
    setShowBanConfirmModal(false);
    setMerchantToBan(null);
  };

  const handleActionsUnban = (merchantId: number) => {
    closeActionsDropdown();
    const m = merchants.find((x) => x.id === merchantId);
    setMerchantToUnban({ id: merchantId, name: m?.ownerName || `Merchant #${merchantId}` });
    setShowUnbanConfirmModal(true);
  };

  const handleCloseUnbanConfirmModal = () => {
    setShowUnbanConfirmModal(false);
    setMerchantToUnban(null);
  };

  const handleConfirmUnban = async () => {
    if (!merchantToUnban) return;
    setSubmitting(true);
    try {
      const response = await api.put(`/merchants/${merchantToUnban.id}/unban`);
      if (response.data.success) {
        handleCloseUnbanConfirmModal();
        await fetchMerchantsData();
        setSuccessModalTitle('Merchant Unbanned');
        setSuccessModalMessage('This merchant has been unbanned. They can log in again using Mobile OTP or Email + Password.');
        setSuccessModalData([
          { label: 'Merchant ID', value: merchantToUnban.id, type: 'id' },
          { label: 'Name', value: merchantToUnban.name, type: 'text' }
        ]);
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to unban merchant');
      }
    } catch (error: any) {
      console.error('Error unbanning merchant:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to unban merchant.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmBan = async () => {
    if (!merchantToBan) return;
    setSubmitting(true);
    try {
      const response = await api.put(`/merchants/${merchantToBan.id}/ban`);
      if (response.data.success) {
        handleCloseBanConfirmModal();
        await fetchMerchantsData();
        setSuccessModalTitle('Merchant Banned');
        setSuccessModalMessage('This merchant has been banned. They will not be able to log in using Mobile OTP or Email + Password, and will not receive OTP.');
        setSuccessModalData([
          { label: 'Merchant ID', value: merchantToBan.id, type: 'id' },
          { label: 'Name', value: merchantToBan.name, type: 'text' }
        ]);
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to ban merchant');
      }
    } catch (error: any) {
      console.error('Error banning merchant:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to ban merchant.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleActionsDelete = (merchantId: number) => {
    closeActionsDropdown();
    const m = merchants.find((x) => x.id === merchantId);
    setMerchantToDelete({ id: merchantId, name: m?.ownerName || `Merchant #${merchantId}` });
    setShowDeleteConfirmModal(true);
  };

  const handleCloseDeleteConfirmModal = () => {
    setShowDeleteConfirmModal(false);
    setMerchantToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!merchantToDelete) return;
    setSubmitting(true);
    try {
      const response = await api.delete(`/merchants/${merchantToDelete.id}`);
      if (response.data.success) {
        handleCloseDeleteConfirmModal();
        await fetchMerchantsData();
        setSuccessModalTitle('Merchant Deleted');
        setSuccessModalMessage('This merchant has been deleted successfully.');
        setSuccessModalData([
          { label: 'Merchant ID', value: merchantToDelete.id, type: 'id' },
          { label: 'Name', value: merchantToDelete.name, type: 'text' }
        ]);
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to delete merchant');
      }
    } catch (error: any) {
      console.error('Error deleting merchant:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to delete merchant.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    // Validate password length
    if (changePasswordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    setSubmitting(true);

    try {
      console.log('Sending password change request:', {
        merchantId: selectedMerchantId,
        newPassword: changePasswordData.newPassword,
        passwordLength: changePasswordData.newPassword.length
      });
      
      const response = await api.put(`/merchants/${selectedMerchantId}`, {
        newPassword: changePasswordData.newPassword
      });

      if (response.data.success) {
        // Close modal and reset form
        handleCloseChangePasswordModal();
        
        // Show success modal
        setSuccessModalTitle('Password Changed Successfully!');
        setSuccessModalMessage('Merchant password has been updated successfully.');
        setSuccessModalData([
          {
            label: 'Merchant',
            value: selectedMerchant.name,
            type: 'text'
          },
          {
            label: 'Email',
            value: selectedMerchant.email,
            type: 'text'
          }
        ]);
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to update password');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error changing password. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      // fetchMerchantsData will be called automatically by useEffect
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
        <ManagerHeader
          user={user}
          onRefresh={() => {}}
          onCreditMoney={() => setShowAddMoneyModal(true)}
          onMyProfile={() => window.location.href = '/manager/account'}
          onLogout={logout}
        />
        <div className="flex flex-1">
          <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}>
            <ManagerSidebar activeTab="merchants" />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-8 backdrop-blur-sm" style={{ backgroundColor: contentColor }}>
            <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
            <p className="text-slate-200 text-base m-0">Loading Merchants...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
      {/* Mobile Sidebar */}
      <ManagerMobileSidebar
        showMobileSidebar={showMobileSidebar}
        setShowMobileSidebar={setShowMobileSidebar}
        activeTab="merchants"
        onCreditMoney={() => setShowAddMoneyModal(true)}
        onLogout={logout}
      />

      {/* Header */}
      <ManagerHeader
        user={user}
        onRefresh={() => window.location.reload()}
        onCreditMoney={() => console.log('Credit Money')}
        onMyProfile={() => window.location.href = '/manager/account'}
        onLogout={() => logout()}
      />
      
      <div className="flex flex-1">
        <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}>
          <ManagerSidebar activeTab="merchants" />
        </div>
        <div className="flex-1 flex flex-col text-slate-200 p-6 md:p-8" style={{ backgroundColor: contentColor }}>
          <div className="flex-1 p-8">
          <div className="flex justify-between items-center mb-6 gap-5 flex-wrap">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-100 m-0">Merchants</h2>
              <p className="text-slate-400 text-sm font-medium mt-1 m-0">Total Merchants: {totalMerchants}</p>
            </div>
            <button
              type="button"
              className="flex items-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 text-white border-none text-sm font-semibold cursor-pointer transition-all shadow-md hover:-translate-y-0.5 hover:shadow-lg"
              onClick={handleCreateMerchantClick}
            >
              <span>+</span> Create New Merchant
            </button>
          </div>

          <div className="rounded-2xl p-5 mb-6 border border-white/[0.06] shadow-xl shadow-black/25" style={{ backgroundColor: sectionColor }}>
            <span className="text-base font-semibold text-slate-300 mr-4">Quick Filters:</span>
            <div className="flex gap-2 mt-2 flex-wrap">
              {quickFilters.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  className={`flex items-center gap-2 py-2.5 px-4 rounded-lg border-2 text-sm font-medium cursor-pointer transition-all ${
                    selectedFilter === filter.label
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-slate-800/80 border-white/[0.12] text-slate-200 hover:border-violet-500 hover:text-violet-300'
                  }`}
                  onClick={() => handleFilterSelect(filter.label)}
                >
                  {filter.icon && <span>{filter.icon}</span>}
                  <span>{filter.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl shadow-black/20" style={{ backgroundColor: tableColor }}>
            <div className="flex flex-wrap justify-between items-center gap-4 py-4 px-6 border-b border-white/[0.1] bg-slate-700/60">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold text-slate-200 m-0">Merchant Management</h3>
                <span className="h-px w-12 bg-gradient-to-r from-white/20 to-transparent rounded-full" aria-hidden="true" />
              </div>
              <div className="flex gap-1 items-center">
                <button
                  type="button"
                  className="w-9 h-9 border border-white/[0.12] bg-slate-800/80 text-slate-200 rounded-md font-semibold cursor-pointer transition-all flex items-center justify-center text-sm hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ←
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`w-9 h-9 border rounded-md font-semibold cursor-pointer transition-all flex items-center justify-center text-sm ${
                      currentPage === page
                        ? 'bg-violet-600 text-white border-violet-500 shadow-md'
                        : 'bg-slate-800/80 border-white/[0.12] text-slate-200 hover:bg-white/10'
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="w-9 h-9 border border-white/[0.12] bg-slate-800/80 text-slate-200 rounded-md font-semibold cursor-pointer transition-all flex items-center justify-center text-sm hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  →
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-700/60">
                    <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Merchant Id</th>
                    <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Owner Name</th>
                    <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Email</th>
                    <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Wallet</th>
                    <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Account Status</th>
                    <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {merchants.map((merchant, index) => (
                    <tr
                      key={merchant.id}
                      className={`border-b border-white/[0.06] transition-colors hover:bg-white/[0.06] ${index % 2 === 1 ? 'bg-white/[0.02]' : ''}`}
                    >
                      <td className="py-3.5 px-4 text-slate-300 align-middle font-medium">{merchant.id}</td>
                      <td className="py-3.5 px-4 text-slate-200 font-medium align-middle">{merchant.ownerName}</td>
                      <td className="py-3.5 px-4 text-slate-300 align-middle text-[13px]">{merchant.email}</td>
                      <td className="py-3.5 px-4 text-slate-200 align-middle">{formatCurrency(merchant.wallet)}</td>
                      <td className="py-3.5 px-4 align-middle">
                        <span className={`inline-flex items-center py-1 px-2.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                          merchant.status === 'active'
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                            : merchant.status === 'banned'
                              ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                              : 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                        }`}>
                          {merchant.status === 'active' ? 'Active' : merchant.status === 'banned' ? 'Banned' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 border-b border-white/[0.06] align-middle">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className={`py-2 px-4 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                              merchant.addMoneyEnabled !== false
                                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-none cursor-pointer hover:opacity-90 hover:shadow-md'
                                : 'bg-slate-600/60 text-slate-400 border border-slate-500/40 cursor-not-allowed opacity-70'
                            }`}
                            onClick={() => merchant.addMoneyEnabled !== false && handleAddMoney(merchant.id)}
                            disabled={merchant.addMoneyEnabled === false}
                            title={merchant.addMoneyEnabled === false ? 'Add Money is disabled for this merchant' : 'Add money to merchant wallet'}
                          >
                            Add Money To Merchant
                          </button>
                          <button
                            type="button"
                            className="py-2 px-4 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white border-none text-xs font-semibold cursor-pointer transition-all hover:opacity-90 hover:shadow-md uppercase tracking-wider"
                            onClick={() => handleChangePassword(merchant.id)}
                          >
                            Change Password
                          </button>
                          <div className="relative inline-block">
                            <button
                              type="button"
                              className="py-2 px-4 rounded-lg bg-slate-600 hover:bg-slate-500 text-white border border-white/[0.12] text-xs font-semibold cursor-pointer transition-all uppercase tracking-wider flex items-center gap-1.5"
                              onClick={(e) => openActionsId === merchant.id ? closeActionsDropdown() : openActionsDropdown(merchant.id, e)}
                              aria-expanded={openActionsId === merchant.id}
                              aria-haspopup="true"
                            >
                              Actions
                              <span className="text-[10px]">▼</span>
                            </button>
                            {openActionsId === merchant.id && actionsAnchorRect && typeof document !== 'undefined' && document.body &&
                              ReactDOM.createPortal(
                                <>
                                  <div className="fixed inset-0 z-[9998]" aria-hidden="true" onClick={closeActionsDropdown} />
                                  <div
                                    className="fixed z-[9999] min-w-[140px] py-1 rounded-lg border border-white/[0.12] shadow-xl"
                                    style={{
                                      backgroundColor: '#334155',
                                      top: actionsAnchorRect.bottom + 4,
                                      left: Math.min(actionsAnchorRect.right - 140, window.innerWidth - 148),
                                      right: 'auto'
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className="w-full text-left py-2 px-4 text-xs text-slate-200 hover:bg-white/10 cursor-pointer"
                                      onClick={() => handleActionsEdit(merchant.id)}
                                    >
                                      Edit
                                    </button>
                                    {merchant.status === 'banned' ? (
                                      <button
                                        type="button"
                                        className="w-full text-left py-2 px-4 text-xs text-emerald-400 hover:bg-emerald-500/20 cursor-pointer"
                                        onClick={() => handleActionsUnban(merchant.id)}
                                      >
                                        Unban
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="w-full text-left py-2 px-4 text-xs text-slate-200 hover:bg-white/10 cursor-pointer"
                                        onClick={() => handleActionsBan(merchant.id)}
                                      >
                                        Ban
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className="w-full text-left py-2 px-4 text-xs text-red-400 hover:bg-red-500/20 cursor-pointer"
                                      onClick={() => handleActionsDelete(merchant.id)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </>,
                                document.body
                              )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />

      {showCreateMerchantModal && (
        <div id="create-merchant-modal-overlay" className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseCreateMerchantModal}>
          <div id="create-merchant-modal-content" className="bg-slate-800 rounded-xl max-w-3xl w-full min-w-[320px] max-h-[90vh] overflow-y-auto shadow-2xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-white/[0.1] bg-slate-700/60">
              <h2 className="text-lg font-bold text-slate-100 m-0">Create New Merchant</h2>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 text-2xl" onClick={handleCloseCreateMerchantModal}>✕</button>
            </div>
            <form onSubmit={handleSubmitMerchant} className="p-6">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-slate-200 m-0 mb-3 pb-1.5 border-b-2 border-white/[0.1]">Merchant Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="flex flex-col">
                    <label htmlFor="name" className="font-semibold text-slate-300 text-sm mb-1">Full Name *</label>
                    <input type="text" id="name" value={merchantFormData.name} onChange={(e) => handleInputChange('name', e.target.value)} className={`w-full py-2.5 px-3.5 border rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 ${formErrors.name ? 'border-red-500' : 'border-white/[0.12] focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20'}`} placeholder="Enter full name" />
                    {formErrors.name && <span className="text-red-400 text-xs mt-1 font-medium">{formErrors.name}</span>}
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="email" className="font-semibold text-slate-300 text-sm mb-1">Email Address *</label>
                    <input type="email" id="email" value={merchantFormData.email} onChange={(e) => handleInputChange('email', e.target.value)} className={`w-full py-2.5 px-3.5 border rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 ${formErrors.email ? 'border-red-500' : 'border-white/[0.12] focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20'}`} placeholder="Enter email address" />
                    {formErrors.email && <span className="text-red-400 text-xs mt-1 font-medium">{formErrors.email}</span>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="flex flex-col">
                    <label htmlFor="password" className="font-semibold text-slate-300 text-sm mb-1">Password *</label>
                    <input type="password" id="password" value={merchantFormData.password} onChange={(e) => handleInputChange('password', e.target.value)} className={`w-full py-2.5 px-3.5 border rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 ${formErrors.password ? 'border-red-500' : 'border-white/[0.12] focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20'}`} placeholder="Enter password (min 6 characters)" />
                    {formErrors.password && <span className="text-red-400 text-xs mt-1 font-medium">{formErrors.password}</span>}
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="phone" className="font-semibold text-slate-300 text-sm mb-1">Mobile Number *</label>
                    <input type="tel" id="phone" value={merchantFormData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className={`w-full py-2.5 px-3.5 border rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 ${formErrors.phone ? 'border-red-500' : 'border-white/[0.12] focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20'}`} placeholder="Enter 10-digit mobile number" maxLength={10} />
                    {formErrors.phone && <span className="text-red-400 text-xs mt-1 font-medium">{formErrors.phone}</span>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="flex flex-col">
                    <label htmlFor="client_id" className="font-semibold text-slate-300 text-sm mb-1">Client ID <span className="text-xs text-slate-500">(from Manager)</span></label>
                    <div className="flex items-center gap-2 border border-white/[0.08] rounded-lg bg-slate-700/60 overflow-hidden min-h-[42px]">
                      <span className="inline-flex items-center justify-center pl-3 text-amber-400 shrink-0 w-6" aria-hidden>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </span>
                      <input type="text" id="client_id" value={merchantFormData.client_id} readOnly className="flex-1 min-w-0 py-2.5 pr-3.5 border-0 bg-transparent text-sm font-medium text-slate-400 cursor-not-allowed placeholder:text-slate-500 focus:ring-0 focus:outline-none" placeholder="Manager's Client ID will be used" />
                    </div>
                    {!merchantFormData.client_id && <span className="text-amber-400 text-xs mt-1">⚠️ Manager Client ID not set</span>}
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="client_secret" className="font-semibold text-slate-300 text-sm mb-1">Client Secret <span className="text-xs text-slate-500">(from Manager)</span></label>
                    <div className="flex items-center gap-2 border border-white/[0.08] rounded-lg bg-slate-700/60 overflow-hidden min-h-[42px]">
                      <span className="inline-flex items-center justify-center pl-3 text-amber-400 shrink-0 w-6" aria-hidden>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </span>
                      <input type="password" id="client_secret" value={merchantFormData.client_secret} readOnly className="flex-1 min-w-0 py-2.5 pr-3.5 border-0 bg-transparent text-sm font-medium text-slate-400 cursor-not-allowed placeholder:text-slate-500 focus:ring-0 focus:outline-none" placeholder="Manager's Client Secret will be used" />
                    </div>
                    {!merchantFormData.client_secret && <span className="text-amber-400 text-xs mt-1">⚠️ Manager Client Secret not set</span>}
                  </div>
                </div>
                <div className="flex flex-col">
                  <label htmlFor="api_key" className="font-semibold text-slate-300 text-sm mb-1">API Key <span className="text-xs text-slate-500">(from Manager)</span></label>
                  <div className="flex items-center gap-2 border border-white/[0.08] rounded-lg bg-slate-700/60 overflow-hidden min-h-[42px]">
                    <span className="inline-flex items-center justify-center pl-3 text-amber-400 shrink-0 w-6" aria-hidden>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </span>
                    <input type="text" id="api_key" value={merchantFormData.api_key} readOnly className="flex-1 min-w-0 py-2.5 pr-3.5 border-0 bg-transparent text-sm font-medium text-slate-400 cursor-not-allowed placeholder:text-slate-500 focus:ring-0 focus:outline-none" placeholder="Manager's API Key will be used" />
                  </div>
                  {!merchantFormData.api_key && <span className="text-amber-400 text-xs mt-1">⚠️ Manager API Key not set</span>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6 pt-5 border-t border-white/[0.1]">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/40 border border-white/[0.08]">
                    <span className="font-semibold text-slate-300 text-sm">Viyona Payout</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={merchantFormData.viyonaEnabled}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${merchantFormData.viyonaEnabled ? 'bg-violet-500' : 'bg-slate-600'}`}
                      onClick={() => handleInputChange('viyonaEnabled', !merchantFormData.viyonaEnabled)}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${merchantFormData.viyonaEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/40 border border-white/[0.08]">
                    <span className="font-semibold text-slate-300 text-sm">PoolPay Payout</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={merchantFormData.poolpayEnabled}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${merchantFormData.poolpayEnabled ? 'bg-violet-500' : 'bg-slate-600'}`}
                      onClick={() => handleInputChange('poolpayEnabled', !merchantFormData.poolpayEnabled)}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${merchantFormData.poolpayEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/40 border border-white/[0.08]">
                    <span className="font-semibold text-slate-300 text-sm">Add Money To Merchant</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={merchantFormData.addMoneyEnabled}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${merchantFormData.addMoneyEnabled ? 'bg-violet-500' : 'bg-slate-600'}`}
                      onClick={() => handleInputChange('addMoneyEnabled', !merchantFormData.addMoneyEnabled)}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${merchantFormData.addMoneyEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Disable a toggle to hide that option from the merchant dashboard. Disable "Add Money To Merchant" to hide the Add Money button in Manager Merchant Management.</p>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-white/[0.1] mt-2">
                <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer transition-all bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20" onClick={handleCloseCreateMerchantModal}>Cancel</button>
                <button type="submit" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer transition-all bg-gradient-to-r from-violet-500 to-violet-600 text-white border-none hover:opacity-90 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed" disabled={submitting}>{submitting ? 'Creating...' : 'Create Merchant'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddMoneyModal && selectedMerchant && (
        <div id="add-coins-modal-overlay" className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseAddMoneyModal}>
          <div id="add-coins-modal-content" className="bg-slate-800 rounded-xl max-w-2xl w-full min-w-[320px] max-h-[90vh] overflow-y-auto shadow-2xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-white/[0.1] bg-slate-700/60">
              <h2 className="text-lg font-bold text-slate-100 m-0">Add Coins to Merchant</h2>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 text-2xl" onClick={handleCloseAddMoneyModal}>×</button>
            </div>
            <div className="p-5">
              <div className="bg-slate-700/40 border border-white/[0.08] rounded-xl p-5 mb-5">
                <h3 className="text-base font-bold text-slate-200 m-0 mb-2">{selectedMerchant.name}</h3>
                <p className="text-sm text-slate-300 m-0"><strong className="text-slate-400">Email:</strong> {selectedMerchant.email}</p>
                <p className="text-sm text-slate-300 m-0"><strong className="text-slate-400">Current Coin Wallet Balance:</strong> {selectedMerchant.wallet_balance || 0}</p>
              </div>
              <div className="bg-slate-700/40 border border-white/[0.08] rounded-xl p-5 mb-5 text-center">
                <h4 className="text-base font-semibold text-slate-200 m-0 mb-1">Wallet Balance (Receive From Admin)</h4>
                <p className="text-2xl font-bold text-slate-100 m-0">{Math.floor(Number(managerWalletBalance))}</p>
                <small className="text-sm text-slate-400 italic m-0">
                  {addMoneyData.type === 'credit' ? 'This amount will be deducted from your wallet' : 'This amount will be added to your wallet'}
                </small>
              </div>
              <form onSubmit={handleAddMoneySubmit}>
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-slate-200 m-0 mb-3 pb-1.5 border-b-2 border-white/[0.1]">Transaction Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div className="flex flex-col">
                      <label htmlFor="transactionType" className="font-semibold text-slate-300 text-sm mb-1">Transaction Type *</label>
                      <select id="transactionType" value={addMoneyData.type} onChange={(e) => setAddMoneyData(prev => ({ ...prev, type: e.target.value as 'credit' | 'debit' }))} className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20" required>
                        <option value="credit">💚 Credit Coin</option>
                        <option value="debit">❤️ Debit Coin</option>
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label htmlFor="amount" className="font-semibold text-slate-300 text-sm mb-1">Amount to transfer (Coin) *</label>
                      <input type="number" id="amount" value={addMoneyData.amount} onChange={(e) => setAddMoneyData(prev => ({ ...prev, amount: e.target.value }))} placeholder="Enter amount" min="0.01" step="0.01" className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20" required />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="description" className="font-semibold text-slate-300 text-sm mb-1">Description</label>
                    <textarea id="description" value={addMoneyData.description} onChange={(e) => setAddMoneyData(prev => ({ ...prev, description: e.target.value }))} placeholder="Enter transaction description (optional)" rows={3} className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 resize-y min-h-[80px]" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-white/[0.1]">
                  <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20" onClick={handleCloseAddMoneyModal}>Cancel</button>
                  <button type="submit" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-gradient-to-r from-violet-500 to-violet-600 text-white border-none hover:opacity-90 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed" disabled={submitting}>{submitting ? 'Processing...' : `${addMoneyData.type === 'credit' ? 'Add' : 'Deduct'} Money`}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditMerchantModal && selectedMerchant && (
        <div id="edit-merchant-modal-overlay" className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseEditMerchantModal}>
          <div id="edit-merchant-modal-content" className="bg-slate-800 rounded-xl max-w-3xl w-full min-w-[320px] max-h-[90vh] overflow-y-auto shadow-2xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-white/[0.1] bg-slate-700/60">
              <h2 className="text-lg font-bold text-slate-100 m-0">Edit Merchant Details</h2>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 text-2xl" onClick={handleCloseEditMerchantModal}>✕</button>
            </div>
            <form onSubmit={handleEditMerchantSubmit} className="p-6">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-slate-200 m-0 mb-3 pb-1.5 border-b-2 border-white/[0.1]">Merchant Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="flex flex-col">
                    <label htmlFor="editName" className="font-semibold text-slate-300 text-sm mb-1">Full Name *</label>
                    <input type="text" id="editName" value={editMerchantData.name} onChange={(e) => setEditMerchantData(prev => ({ ...prev, name: e.target.value }))} required className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 placeholder-slate-500" placeholder="Enter full name" />
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="editEmail" className="font-semibold text-slate-300 text-sm mb-1">Email Address *</label>
                    <input type="email" id="editEmail" value={editMerchantData.email} readOnly className="w-full py-2.5 px-3.5 border border-white/[0.08] rounded-lg text-sm font-medium text-slate-400 bg-slate-700/60 cursor-not-allowed" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="flex flex-col">
                    <label htmlFor="editPassword" className="font-semibold text-slate-300 text-sm mb-1">Password *</label>
                    <input type="password" id="editPassword" value="••••••••••" readOnly className="w-full py-2.5 px-3.5 border border-white/[0.08] rounded-lg text-sm font-medium text-slate-400 bg-slate-700/60 cursor-not-allowed" />
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="editPhone" className="font-semibold text-slate-300 text-sm mb-1">Mobile Number *</label>
                    <input type="tel" id="editPhone" value={editMerchantData.phone != null ? String(editMerchantData.phone) : ''} onChange={(e) => setEditMerchantData(prev => ({ ...prev, phone: e.target.value }))} required className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 placeholder-slate-500" placeholder="Enter 10-digit mobile number" maxLength={10} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="flex flex-col">
                    <label htmlFor="editClientId" className="font-semibold text-slate-300 text-sm mb-1">Client ID <span className="text-xs text-slate-500">(saved in user_detail)</span></label>
                    <div className="flex items-center gap-2 border border-white/[0.08] rounded-lg bg-slate-700/60 overflow-hidden min-h-[42px]">
                      <span className="inline-flex items-center justify-center pl-3 text-amber-400 shrink-0 w-6" aria-hidden>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </span>
                      <input type="text" id="editClientId" value={editMerchantData.clientId || '—'} readOnly className="flex-1 min-w-0 py-2.5 pr-3.5 border-0 bg-transparent text-sm font-medium text-slate-400 cursor-not-allowed focus:ring-0 focus:outline-none" />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="editClientSecret" className="font-semibold text-slate-300 text-sm mb-1">Client Secret <span className="text-xs text-slate-500">(saved in user_detail)</span></label>
                    <div className="flex items-center gap-2 border border-white/[0.08] rounded-lg bg-slate-700/60 overflow-hidden min-h-[42px]">
                      <span className="inline-flex items-center justify-center pl-3 text-amber-400 shrink-0 w-6" aria-hidden>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </span>
                      <input type="password" id="editClientSecret" value={editMerchantData.clientSecret ? '••••••••••' : '—'} readOnly className="flex-1 min-w-0 py-2.5 pr-3.5 border-0 bg-transparent text-sm font-medium text-slate-400 cursor-not-allowed focus:ring-0 focus:outline-none" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col">
                  <label htmlFor="editApiKey" className="font-semibold text-slate-300 text-sm mb-1">API Key <span className="text-xs text-slate-500">(saved in user_detail)</span></label>
                  <div className="flex items-center gap-2 border border-white/[0.08] rounded-lg bg-slate-700/60 overflow-hidden min-h-[42px]">
                    <span className="inline-flex items-center justify-center pl-3 text-amber-400 shrink-0 w-6" aria-hidden>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </span>
                    <input type="text" id="editApiKey" value={editMerchantData.apiKey || '—'} readOnly className="flex-1 min-w-0 py-2.5 pr-3.5 border-0 bg-transparent text-sm font-medium text-slate-400 cursor-not-allowed focus:ring-0 focus:outline-none" />
                  </div>
                </div>
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 mt-6 pt-5 border-t border-white/[0.1] ${editMerchantData.adminControlsPayoutOptions ? 'opacity-60 pointer-events-none' : ''}`}>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/40 border border-white/[0.08]">
                    <span className="font-semibold text-slate-300 text-sm">Viyona Payout</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={editMerchantData.viyonaEnabled}
                      disabled={editMerchantData.adminControlsPayoutOptions}
                      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${editMerchantData.adminControlsPayoutOptions ? 'cursor-not-allowed' : 'cursor-pointer'} ${editMerchantData.viyonaEnabled ? 'bg-violet-500' : 'bg-slate-600'}`}
                      onClick={() => !editMerchantData.adminControlsPayoutOptions && setEditMerchantData(prev => ({ ...prev, viyonaEnabled: !prev.viyonaEnabled }))}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${editMerchantData.viyonaEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/40 border border-white/[0.08]">
                    <span className="font-semibold text-slate-300 text-sm">PoolPay Payout</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={editMerchantData.poolpayEnabled}
                      disabled={editMerchantData.adminControlsPayoutOptions}
                      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${editMerchantData.adminControlsPayoutOptions ? 'cursor-not-allowed' : 'cursor-pointer'} ${editMerchantData.poolpayEnabled ? 'bg-violet-500' : 'bg-slate-600'}`}
                      onClick={() => !editMerchantData.adminControlsPayoutOptions && setEditMerchantData(prev => ({ ...prev, poolpayEnabled: !prev.poolpayEnabled }))}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${editMerchantData.poolpayEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/40 border border-white/[0.08]">
                    <span className="font-semibold text-slate-300 text-sm">Add Money To Merchant</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={editMerchantData.addMoneyEnabled}
                      disabled={editMerchantData.adminControlsPayoutOptions}
                      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${editMerchantData.adminControlsPayoutOptions ? 'cursor-not-allowed' : 'cursor-pointer'} ${editMerchantData.addMoneyEnabled ? 'bg-violet-500' : 'bg-slate-600'}`}
                      onClick={() => !editMerchantData.adminControlsPayoutOptions && setEditMerchantData(prev => ({ ...prev, addMoneyEnabled: !prev.addMoneyEnabled }))}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${editMerchantData.addMoneyEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Disable a toggle to hide that option. Disable &quot;Add Money To Merchant&quot; to show the Add Money button as disabled in Manager Merchant Management.</p>
                {editMerchantData.adminControlsPayoutOptions && (
                  <p className="text-sm text-amber-400 font-medium mt-3 flex items-center gap-2">
                    <span>🔒</span> Admin controls these options. You cannot change them.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-white/[0.1] mt-2">
                <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer transition-all bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20" onClick={handleCloseEditMerchantModal}>Cancel</button>
                <button type="submit" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer transition-all bg-gradient-to-r from-violet-500 to-violet-600 text-white border-none hover:opacity-90 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed" disabled={submitting}>{submitting ? 'Updating...' : 'Update Merchant'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showChangePasswordModal && selectedMerchant && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseChangePasswordModal}>
          <div className="bg-slate-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-white/[0.1] bg-slate-700/60">
              <h2 className="text-lg font-bold text-slate-100 m-0">Change Merchant Password</h2>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 text-2xl" onClick={handleCloseChangePasswordModal}>×</button>
            </div>
            <div className="p-5">
              <div className="bg-slate-700/40 border border-white/[0.08] rounded-xl p-5 mb-5">
                <h3 className="text-base font-bold text-slate-200 m-0 mb-2">Merchant Details</h3>
                <p className="text-sm text-slate-300 m-0"><strong className="text-slate-400">Name:</strong> {selectedMerchant.name}</p>
                <p className="text-sm text-slate-300 m-0"><strong className="text-slate-400">Email:</strong> {selectedMerchant.email}</p>
                <p className="text-sm text-slate-300 m-0"><strong className="text-slate-400">ID:</strong> {selectedMerchant.id}</p>
              </div>
              <form onSubmit={handleChangePasswordSubmit}>
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-slate-200 m-0 mb-3 pb-1.5 border-b-2 border-white/[0.1]">Change Password</h3>
                  <div className="flex flex-col mb-3">
                    <label htmlFor="newPassword" className="font-semibold text-slate-300 text-sm mb-1">New Password *</label>
                    <input type="password" id="newPassword" value={changePasswordData.newPassword} onChange={(e) => setChangePasswordData(prev => ({ ...prev, newPassword: e.target.value }))} placeholder="Enter new password (min 6 characters)" required minLength={6} className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20" />
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="confirmPassword" className="font-semibold text-slate-300 text-sm mb-1">Confirm New Password *</label>
                    <input type="password" id="confirmPassword" value={changePasswordData.confirmPassword} onChange={(e) => setChangePasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))} placeholder="Confirm new password" required minLength={6} className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-white/[0.1]">
                  <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20" onClick={handleCloseChangePasswordModal}>Cancel</button>
                  <button type="submit" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-gradient-to-r from-violet-500 to-violet-600 text-white border-none hover:opacity-90 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed" disabled={submitting}>{submitting ? 'Changing...' : 'Change Password'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showBanConfirmModal && merchantToBan && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseBanConfirmModal}>
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-white/[0.1] bg-slate-700/60">
              <h2 className="text-lg font-bold text-slate-100 m-0">Ban Merchant?</h2>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 text-2xl" onClick={handleCloseBanConfirmModal}>×</button>
            </div>
            <div className="p-5">
              <p className="text-slate-300 m-0 mb-4">
                Are you sure you want to ban <strong className="text-slate-200">{merchantToBan.name}</strong>? They will not be able to log in using Mobile OTP or Email + Password, and will not receive OTP. When they try to log in, they will see: &quot;Your account is banned.&quot;
              </p>
            </div>
            <div className="flex justify-end gap-2 p-5 pt-0 border-t border-white/[0.1]">
              <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20" onClick={handleCloseBanConfirmModal}>Cancel</button>
              <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-red-600 hover:bg-red-700 text-white border-none disabled:opacity-60 disabled:cursor-not-allowed" onClick={handleConfirmBan} disabled={submitting}>{submitting ? 'Banning...' : 'Ban'}</button>
            </div>
          </div>
        </div>
      )}

      {showUnbanConfirmModal && merchantToUnban && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseUnbanConfirmModal}>
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-white/[0.1] bg-slate-700/60">
              <h2 className="text-lg font-bold text-slate-100 m-0">Unban Merchant?</h2>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 text-2xl" onClick={handleCloseUnbanConfirmModal}>×</button>
            </div>
            <div className="p-5">
              <p className="text-slate-300 m-0 mb-4">
                Are you sure you want to unban <strong className="text-slate-200">{merchantToUnban.name}</strong>? They will be able to log in again using Mobile OTP or Email + Password and will receive OTP.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-5 pt-0 border-t border-white/[0.1]">
              <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20" onClick={handleCloseUnbanConfirmModal}>Cancel</button>
              <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white border-none disabled:opacity-60 disabled:cursor-not-allowed" onClick={handleConfirmUnban} disabled={submitting}>{submitting ? 'Unbanning...' : 'Unban'}</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmModal && merchantToDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseDeleteConfirmModal}>
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-white/[0.1] bg-slate-700/60">
              <h2 className="text-lg font-bold text-slate-100 m-0">Delete Merchant?</h2>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 text-2xl" onClick={handleCloseDeleteConfirmModal}>×</button>
            </div>
            <div className="p-5">
              <p className="text-slate-300 m-0 mb-4">
                Are you sure you want to delete <strong className="text-slate-200">{merchantToDelete.name}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-5 pt-0 border-t border-white/[0.1]">
              <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20" onClick={handleCloseDeleteConfirmModal}>Cancel</button>
              <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-red-600 hover:bg-red-700 text-white border-none disabled:opacity-60 disabled:cursor-not-allowed" onClick={handleConfirmDelete} disabled={submitting}>{submitting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={successModalTitle}
        message={successModalMessage}
        details={successModalData}
      />
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ManagerMerchants;
